// LM libre – autres prestations (sans assurance) — demande d'Anthony du
// 20/10/2026. LM à saisie libre (EXPERTISE / CONSEIL), pour les « Autres
// prestations fournies à l'entité » du référentiel des missions de
// l'expert-comptable (missions sans assurance), quand aucune autre LM ne
// convient.
//
// Données propres au modèle Word (public/LM libre autres prestations.docx),
// ajoutées par generateDocument (SignModif.js) au moment de la génération.
// Balises du modèle : {nom_mission}, {description_mission}, {livrable},
// {descriptif_mission}, {#avec_autres_informations}{autres_informations},
// {#travaux}{.}, {#exclusions}{.}, {#lignes_honoraires}{libelle}{montant},
// {total_honoraires}, {TVA_honoraires_pourcentage}, {TVA_honoraires},
// {total_honoraires_ttc}.

export const LM_LIBRE = "LM libre autres prestations.docx";
export const LIBELLE_LM_LIBRE = "LM libre - autres prestations (sans assurance)";

// Signatures DocuSign posées sur les repères invisibles du modèle (texte blanc
// sous « Le client, » et sous « ALFA COMPTABILITE AUDIT ») : la page de
// signature varie avec la saisie libre. Mêmes repères côté backend
// (services/lmSignaturePositions.js).
// Repères « \s1\ » (client) et « \s2\ » (cabinet), en blanc 8 pt dans le Word.
export const SIGNATURE_CLIENT_LIBRE = { anchorString: "\\s1\\", anchorXOffset: 0, anchorYOffset: 0 };
export const SIGNATURE_CABINET_LIBRE = { anchorString: "\\s2\\", anchorXOffset: 0, anchorYOffset: 0 };

const MANQUANT = "[MANQUANT]";
const texte = (v) => String(v ?? "").trim();
const ouManquant = (v) => texte(v) || MANQUANT;

export const nombre = (v) => {
  const n = parseFloat(String(v ?? "").replace(/[\s  €%]/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

// « 1 234,50 € » — même présentation que les autres LM (formatNombreEuro).
export const montantEuro = (n) =>
  n === null || n === undefined
    ? ""
    : `${n
        .toFixed(2)
        .replace(".", ",")
        .replace(/\B(?=(\d{3})+(?!\d))/g, " ")} €`;

// Listes saisies : tableau, ou texte JSON (brouillon enregistré).
function liste(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v.trim().startsWith("[")) {
    try {
      const l = JSON.parse(v);
      return Array.isArray(l) ? l : [];
    } catch (e) {
      return [];
    }
  }
  return [];
}

// Lignes de travaux / d'exclusions : au moins une ligne (vide) pour la saisie.
export const lignesTexte = (v) => {
  const l = liste(v).map((x) => (typeof x === "string" ? x : ""));
  return l.length ? l : [""];
};

// Lignes d'honoraires : [{ libelle, montant }], au moins une ligne.
export const lignesHonoraires = (v) => {
  const l = liste(v).map((x) => ({ libelle: texte(x?.libelle), montant: x?.montant ?? "" }));
  return l.length ? l : [{ libelle: "", montant: "" }];
};

// Totaux HT / TVA / TTC à partir des lignes saisies et du taux de TVA.
export function calculHonorairesLibre(formData) {
  const montants = lignesHonoraires(formData.libre_honoraires)
    .map((l) => nombre(l.montant))
    .filter((n) => n !== null);
  const ht = montants.length ? montants.reduce((s, n) => s + n, 0) : null;
  const taux = nombre(formData.TVA_honoraires_pourcentage);
  const tva = ht === null ? null : (ht * (taux ?? 0)) / 100;
  return { ht, tva, ttc: ht === null ? null : ht + tva };
}

// Nom du document (fichier, sujet du mail de signature, titre de la LM
// soumise) : « LM [nom de la mission] », jamais le nom du modèle. Sans
// caractères spéciaux : le nom sert aussi d'URL (stockage + conversion PDF).
export function nomDocumentLibre(formData) {
  const nom = texte(formData?.nom_mission)
    .replace(/[\\/:*?"<>|'’#%&{}]/g, " ")
    // « Client - Type » : le sujet du mail DocuSign est découpé sur « - »
    // (liste des Lettres de mission), un tiret dans le nom le couperait.
    .replace(/\s+-\s+/g, " – ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
  return nom ? `LM ${nom}` : "LM libre";
}

// Nom affiché d'une LM : nom du modèle sans « .docx », sauf la LM libre.
export const nomDocumentLM = (selectedForm, formData) =>
  selectedForm === LM_LIBRE ? nomDocumentLibre(formData) : String(selectedForm || "").replace(/\.docx$/, "");

export function donneesDocumentLibre(formData) {
  const hono = calculHonorairesLibre(formData);
  const remplies = (l) => l.map(texte).filter(Boolean);
  const travaux = remplies(lignesTexte(formData.libre_travaux));
  const exclusions = remplies(lignesTexte(formData.libre_exclusions));
  const lignes = lignesHonoraires(formData.libre_honoraires).filter((l) => l.libelle || nombre(l.montant) !== null);
  return {
    nom_mission: ouManquant(formData.nom_mission),
    description_mission: ouManquant(formData.description_mission),
    livrable: ouManquant(formData.livrable),
    descriptif_mission: ouManquant(formData.descriptif_mission),
    avec_autres_informations: !!texte(formData.autres_informations),
    autres_informations: texte(formData.autres_informations),
    travaux: travaux.length ? travaux : [MANQUANT],
    exclusions: exclusions.length ? exclusions : [MANQUANT],
    lignes_honoraires: lignes.length
      ? lignes.map((l) => ({ libelle: l.libelle || MANQUANT, montant: montantEuro(nombre(l.montant)) || MANQUANT }))
      : [{ libelle: MANQUANT, montant: MANQUANT }],
    total_honoraires: montantEuro(hono.ht) || MANQUANT,
    TVA_honoraires: montantEuro(hono.tva) || MANQUANT,
    total_honoraires_ttc: montantEuro(hono.ttc) || MANQUANT,
  };
}
